/**
 * permissionsSlice — Settings → Permissions screen state.
 *
 * Holds the four datasets the screen needs (staff, positions, catalog, matrix)
 * in one slice, hydrated from SSR via PermissionsInitializer. Mutations dispatch
 * granular reducers so the UI updates without a full refetch; the server actions
 * also `updateTag(...)` so the next server read is fresh.
 */
import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import type {
  MatrixResource,
  PermissionResource,
  Position,
  StaffRow,
} from "@/components/settings/permissions/types";

export interface PermissionsData {
  staff: StaffRow[];
  positions: Position[];
  catalog: PermissionResource[];
  matrix: MatrixResource[];
}

export interface PermissionsState extends PermissionsData {
  loading: boolean;
}

const initialState: PermissionsState = {
  staff: [],
  positions: [],
  catalog: [],
  matrix: [],
  loading: true,
};

const permissionsSlice = createSlice({
  name: "permissions",
  initialState,
  reducers: {
    /** Hydrate all four datasets at once — used by the Initializer on mount. */
    setPermissionsData(state, action: PayloadAction<PermissionsData>) {
      state.staff = action.payload.staff;
      state.positions = action.payload.positions;
      state.catalog = action.payload.catalog;
      state.matrix = action.payload.matrix;
    },
    setStaff(state, action: PayloadAction<StaffRow[]>) {
      state.staff = action.payload;
    },
    upsertStaff(state, action: PayloadAction<StaffRow>) {
      const i = state.staff.findIndex((s) => s.id === action.payload.id);
      if (i === -1) state.staff.unshift(action.payload);
      else state.staff[i] = action.payload;
    },
    removeStaff(state, action: PayloadAction<string>) {
      state.staff = state.staff.filter((s) => s.id !== action.payload);
    },
    addPosition(state, action: PayloadAction<Position>) {
      if (!state.positions.some((p) => p.id === action.payload.id)) {
        state.positions.push(action.payload);
      }
    },
    setMatrix(state, action: PayloadAction<MatrixResource[]>) {
      state.matrix = action.payload;
    },
    setPermissionsLoading(state, action: PayloadAction<boolean>) {
      state.loading = action.payload;
    },
  },
});

export const {
  setPermissionsData,
  setStaff,
  upsertStaff,
  removeStaff,
  addPosition,
  setMatrix,
  setPermissionsLoading,
} = permissionsSlice.actions;

export default permissionsSlice.reducer;
